import { useState, useEffect, useCallback } from 'react'
import { db, type TopicoProgresso, type DiarioEntry } from '@services/db.js'
import {
  upsertTopicoProgresso,
  addDiarioEntry as dbAddDiario,
  updateDiarioEntry as dbUpdateDiario,
  deleteDiarioEntry as dbDeleteDiario,
  getDiario,
} from '@services/crud.js'
import { getTopicoContent, type TopicoContent } from '@content/topicos-content.js'
import { syncMetadadoParaObra } from '@utils/metadadoObraSync.js'

export interface ChecklistItemState {
  id: string
  texto: string
  concluido: boolean
}

export interface TopicoHookData {
  content: TopicoContent | null
  progresso: TopicoProgresso | null
  diario: DiarioEntry[]
  checklistItems: ChecklistItemState[]
  percentualPratico: number
  isLoading: boolean
}

const INITIAL: TopicoHookData = {
  content: null,
  progresso: null,
  diario: [],
  checklistItems: [],
  percentualPratico: 0,
  isLoading: true,
}

function mergeChecklist(
  contentItems: { id: string; texto: string }[],
  dbItems: { id: string; texto: string; concluido: boolean }[],
): ChecklistItemState[] {
  const dbMap = new Map(dbItems.map((i) => [i.id, i.concluido]))
  return contentItems.map((ci) => ({
    id: ci.id,
    texto: ci.texto,
    concluido: dbMap.get(ci.id) ?? false,
  }))
}

export function useTopico(moduloId: string, topicoId: string) {
  const [data, setData] = useState<TopicoHookData>(INITIAL)

  const load = useCallback(async () => {
    const content = getTopicoContent(topicoId)
    const [progresso, diarioEntries] = await Promise.all([
      db.topicos_progresso.where('topicoId').equals(topicoId).first(),
      getDiario(topicoId),
    ])

    const contentItems = content?.checklistAplicabilidade ?? []
    const checklistItems = mergeChecklist(contentItems, progresso?.checklistItems ?? [])

    const checked = checklistItems.filter((i) => i.concluido).length
    const total = checklistItems.length
    const percentualPratico =
      total > 0 ? Math.round((checked / total) * 100) : (progresso?.percentualPratico ?? 0)

    setData({
      content,
      progresso: progresso ?? null,
      diario: diarioEntries,
      checklistItems,
      percentualPratico,
      isLoading: false,
    })
  }, [topicoId])

  useEffect(() => {
    load()
  }, [load])

  const toggleChecklistItem = useCallback(
    async (itemId: string) => {
      const content = getTopicoContent(topicoId)
      const contentItems = content?.checklistAplicabilidade ?? []
      const current = await db.topicos_progresso.where('topicoId').equals(topicoId).first()
      const currentItems = current?.checklistItems ?? []
      const dbMap = new Map(currentItems.map((i) => [i.id, i.concluido]))

      const newItems = contentItems.map((ci) => ({
        id: ci.id,
        texto: ci.texto,
        concluido: ci.id === itemId ? !(dbMap.get(ci.id) ?? false) : (dbMap.get(ci.id) ?? false),
      }))

      const checked = newItems.filter((i) => i.concluido).length
      const total = newItems.length
      const pct = total > 0 ? Math.round((checked / total) * 100) : 0

      await upsertTopicoProgresso(topicoId, moduloId, {
        checklistItems: newItems,
        percentualPratico: pct,
      })
      await load()
    },
    [moduloId, topicoId, load],
  )

  const saveMetadado = useCallback(
    async (campo: string, valor: string) => {
      const current = await db.topicos_progresso.where('topicoId').equals(topicoId).first()
      const currentMeta = current?.metadados ?? {}
      await upsertTopicoProgresso(topicoId, moduloId, {
        metadados: { ...currentMeta, [campo]: valor },
      })
      await syncMetadadoParaObra(campo, valor)
      await load()
    },
    [moduloId, topicoId, load],
  )

  const addEntradaDiario = useCallback(
    async (texto: string) => {
      if (!texto.trim()) return
      await dbAddDiario({ topicoId, texto: texto.trim() })
      await load()
    },
    [topicoId, load],
  )

  const marcarConcluido = useCallback(async () => {
    await upsertTopicoProgresso(topicoId, moduloId, { concluido: true, percentualPratico: 100 })
    await load()
  }, [moduloId, topicoId, load])

  const desmarcarConcluido = useCallback(async () => {
    await upsertTopicoProgresso(topicoId, moduloId, { concluido: false })
    await load()
  }, [moduloId, topicoId, load])

  const marcarTodosChecklist = useCallback(
    async (marcar: boolean) => {
      const content = getTopicoContent(topicoId)
      const contentItems = content?.checklistAplicabilidade ?? []
      const newItems = contentItems.map((ci) => ({ id: ci.id, texto: ci.texto, concluido: marcar }))
      const pct = marcar ? 100 : 0
      await upsertTopicoProgresso(topicoId, moduloId, {
        checklistItems: newItems,
        percentualPratico: pct,
      })
      await load()
    },
    [moduloId, topicoId, load],
  )

  const editEntradaDiario = useCallback(
    async (id: number, texto: string) => {
      if (!texto.trim()) return
      await dbUpdateDiario(id, texto.trim())
      await load()
    },
    [load],
  )

  const deleteEntradaDiario = useCallback(
    async (id: number) => {
      await dbDeleteDiario(id)
      await load()
    },
    [load],
  )

  return {
    ...data,
    toggleChecklistItem,
    saveMetadado,
    addEntradaDiario,
    marcarConcluido,
    desmarcarConcluido,
    marcarTodosChecklist,
    editEntradaDiario,
    deleteEntradaDiario,
    reload: load,
  }
}
